import React, { useState } from "react";

import { fps } from "~/tools/fps";
import { useDelayed } from "~/tools/use-delayed";
import { useThrottle } from "~/tools/use-throttle";

import { type Point, PositionedPopover } from "./positioned-popover";

const POPOVER_HIDE_DELAY_MS = 500;

export interface FloatingPopoverProps {
	content: React.ReactNode;
	className?: string;
}

export const FloatingPopover: React.FC<React.PropsWithChildren<FloatingPopoverProps>> = ({
	content,
	children,
	...props
}) => {
	const [position, setPosition] = useState<Point | null>(null);
	const [isPopoverVisible, setPopoverVisible] = useState(false);
	const [isHoveringPopover, setHoveringPopover] = useState(false);

	const adjustOffset = useThrottle((e: React.MouseEvent<HTMLDivElement>) => {
		if (e.currentTarget instanceof HTMLElement) {
			const hoverAreaRect = e.currentTarget.getBoundingClientRect();
			setPosition({ x: e.clientX, y: hoverAreaRect.y });
		}
	}, fps(60));

	const isDelayedPopoverVisible = useDelayed(isPopoverVisible, POPOVER_HIDE_DELAY_MS);

	if (!content) {
		return <div {...props}>{children}</div>;
	}

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!isHoveringPopover) {
			adjustOffset(e);
		}
		setPopoverVisible(true);
	};

	const handleMouseLeave = () => {
		if (!isHoveringPopover) {
			setPopoverVisible(false);
		}
	};

	const handlePopoverHover = (isHovered: boolean) => {
		setHoveringPopover(isHovered);
		setPopoverVisible(isHovered);
	};

	return (
		<div onMouseLeave={handleMouseLeave} onMouseMove={handleMouseMove} {...props}>
			{isDelayedPopoverVisible && position && (
				<PositionedPopover offset={{ x: 10, y: 22 }} onHover={handlePopoverHover} position={position}>
					{content}
				</PositionedPopover>
			)}
			{children}
		</div>
	);
};
